/* eslint-disable dot-notation */
import Quill from 'quill';
import type { Delta, EmitterSource, Range } from 'quill/core';
import { Point } from '../../../../platform/geom/Point';
import { Rect } from '../../../../platform/geom/Rect';
import { getMentionCharIndex, hasValidChars, hasValidMentionCharIndex } from './MentionUtil';

const Module = Quill.import('core/module');

export type AtsertWidget = {
    show(props: AtsertWidgetProps): void,
    hide(): void,
};

export type AtsertWidgetAction = 'insert' | 'swap' | 'reject';

export type AtsertWidgetProps = {
    absolutePosition: Point,
    relativePosition: Point,
    denotation: string,
    query: string,
    onAction(action: AtsertWidgetAction, param?: any): void,
};

export interface MentionOption {
    /**
     * Specifies which characters will cause the mention autocomplete to open
     * @default ["@"]
     */
    mentionDenotationChars: string[];

    /**
     * Allowed characters in search term triggering a search request using regular expressions. Can be a function
     * that takes the denotationChar and returns a regex.
     * @default /^[a-zA-Z0-9_]*$/
     */
    allowedChars: RegExp | ((char: string) => RegExp);

    /**
     * Minimum number of characters after the @ symbol triggering a search request
     * @default 0
     */
    minChars: number;

    /**
     * Maximum number of characters after the @ symbol triggering a search request
     * @default 31
     */
    maxChars: number;

    /**
     * Whether or not the denotation character(s) should be isolated. For example, to avoid mentioning in an email.
     * @default false
     */
    isolateCharacter: boolean;

    /**
     * Only works if isolateCharacter is set to true. Whether or not the denotation character(s) can appear inline of the
     * mention text. For example, to allow mentioning an email with the @ symbol as the denotation character.
     * @default false
     */
    allowInlineMentionChar: boolean;

    /**
     * The name of the Quill Blot to be used for inserted mentions. A default implementation is provided named 'mention',
     * which may be overridden with a custom blot.
     * @default "mention"
     */
    blotName: string;

    /**
     * A list of data values you wish to be passed from your list data to the html node. (id, value, denotationChar, link,
     * target are included by default).
     * @default ["id", "value", "denotationChar", "link", "target", "disabled"]
     */
    dataAttributes: string[];

    /**
     * Link target for mentions with a link
     * @default "_blank"
     */
    linkTarget: string;

    /**
     * Whether or not insert 1 space after mention block in text
     * @default true
     */
    spaceAfterInsert: boolean;

    /**
     * Callback when mention dropdown is open.
     * @returns
     */
    onOpen: () => boolean;

    /**
     * Callback before the DOM of mention dropdown is removed.
     * @returns
     */
    onBeforeClose: () => boolean;

    /**
     * Callback when mention dropdown is closed.
     * @returns
     */
    onClose: () => boolean;
    /**
     * Callback for a selected item. When overriding this method, insertItem should be used to insert item to the editor.
     * This makes async requests possible.
     * @param item
     * @param insertItem
     */
    onSelect: (
        item: DOMStringMap,
        insertItem: (
            data: Record<string, unknown>,
            programmaticInsert?: boolean,
            overriddenOptions?: object
        ) => void
    ) => void;

    widget?: AtsertWidget;
}

export class Mention extends Module<MentionOption> {
    static DEFAULTS: MentionOption = {
        mentionDenotationChars: ['@', '#'],
        allowedChars: /^[a-zA-Z0-9_]*$/,
        minChars: 0,
        maxChars: 31,
        isolateCharacter: true,
        allowInlineMentionChar: false,
        blotName: 'mention',
        dataAttributes: [
            'id',
            'value',
            'denotationChar',
            'link',
            'target',
            'disabled',
        ],
        linkTarget: '_blank',
        spaceAfterInsert: true,
        onSelect: (item, insertItem) => insertItem(item),
        onOpen: () => true,
        onBeforeClose: () => true,
        onClose: () => true,
    };

    private isOpen: boolean;
    private cursorPos?: number;

    constructor(quill: Quill, options?: Partial<MentionOption>) {
        super(quill, options);
        this.isOpen = false;

        if (Array.isArray(options?.dataAttributes)) {
            this.options.dataAttributes = this.options.dataAttributes
                ? this.options.dataAttributes.concat(options.dataAttributes)
                : options.dataAttributes;
        }

        // Bind all option-functions so they have a reasonable context
        for (const o in this.options) {
            if (Object.prototype.hasOwnProperty.call(this.options, o)) {
                const key = o;
                const value = this.options[key];
                if (typeof value === 'function') {
                    this.options[key as any] = value.bind(this) as any;
                }
            }
        }

        quill.on('text-change', this.onTextChange.bind(this));
        quill.on('selection-change', this.onSelectionChange.bind(this));

        // Pasting doesn't fire selection-change after the pasted text is
        // inserted, so here we manually trigger one
        quill.container.addEventListener('paste', () => {
            setTimeout(() => {
                const range = quill.getSelection();
                this.onSelectionChange(range);
            });
        });

        // Cancel "Enter" when the widget is open
        quill.keyboard.addBinding(
            { key: 'Enter' },
            () => !this.isOpen
        );

        quill.keyboard.bindings['Enter'].unshift(
            quill.keyboard.bindings['Enter'].pop()!
        );
    }

    showWidget(denotation: string, mentionCharPos: number, query: string) {
        const { widget } = this.options;
        if (widget) {

            const onAction = (action: AtsertWidgetAction, param?: any) => {
                if (action === 'insert') {
                    this.insertItem({
                        denotationChar: '@',
                        index: 0,
                        ...param,
                    }, false, {});
                }
                else if (action === 'swap') {
                    this.swapItem(param?.to);
                }
                else if (action === 'reject') {
                    this.rejectItem();
                }
            };

            const positioning = this.getSuggestedWidgetPositioning(mentionCharPos);
            if (!positioning) return;

            widget.show({ ...positioning, denotation, query, onAction });
        }
        this.setIsOpen(true);
    }

    hideWidget() {
        if (!this.isOpen) return;
        const { widget } = this.options;
        if (widget) {
            widget.hide();
        }
        this.setIsOpen(false);
    }

    insertItem(
        data: { [key: string]: unknown } | null,
        programmaticInsert: boolean,
        overriddenOptions = {}
    ) {

        const [mentionChar, mentionCharPos, textAfter] = this.getMentionCharAndTextInfo() ?? [];
        if (!mentionChar) return;

        const options = { ...this.options, ...overriddenOptions };

        let insertAtPos: number;

        if (!programmaticInsert) {
            insertAtPos = mentionCharPos;
            this.quill.deleteText(
                mentionCharPos,
                this.cursorPos - mentionCharPos,
                Quill.sources.USER
            );
        }
        else {
            insertAtPos = this.cursorPos;
        }

        const delta = this.quill.insertEmbed(
            insertAtPos,
            options.blotName ?? Mention.DEFAULTS.blotName,
            data,
            Quill.sources.USER
        );

        if (options.spaceAfterInsert) {
            this.quill.insertText(insertAtPos + 1, ' ', Quill.sources.USER);
            // setSelection here sets cursor position
            this.quill.setSelection(insertAtPos + 2, Quill.sources.USER);
        }
        else {
            this.quill.setSelection(insertAtPos + 1, Quill.sources.USER);
        }

        this.hideWidget();
        return delta;
    }

    swapItem(toDenotation: string) {

        const [mentionChar, mentionCharPos, textAfter] = this.getMentionCharAndTextInfo() ?? [];
        if (!mentionChar) return;

        this.showWidget(toDenotation, mentionCharPos, textAfter);

        this.quill.deleteText(
            mentionCharPos,
            this.cursorPos - mentionCharPos,
            Quill.sources.USER
        );

        this.quill.insertText(
            mentionCharPos, `${toDenotation}${textAfter}`,
            Quill.sources.USER
        );

        this.quill.setSelection(mentionCharPos + 1, Quill.sources.USER);
    }

    rejectItem() {

        const [mentionChar, mentionCharPos, _textAfter] = this.getMentionCharAndTextInfo() ?? [];
        if (!mentionChar) return;

        this.quill.deleteText(
            mentionCharPos,
            this.cursorPos - mentionCharPos,
            Quill.sources.USER
        );

        this.quill.setSelection(mentionCharPos, Quill.sources.USER);

        this.hideWidget();
    }

    setIsOpen(isOpen: boolean) {
        if (this.isOpen !== isOpen) {
            if (isOpen) {
                this.options.onOpen?.();
            }
            else {
                this.options.onClose?.();
            }
            this.isOpen = isOpen;
        }
    }

    getMentionCharAndTextInfo() {

        const textBeforeCursor = this.getTextBeforeCursor();

        const textOffset = Math.max(0, this.cursorPos - this.options.maxChars!);
        const textPrefix = textOffset
            ? this.quill.getText(textOffset - 1, textOffset)
            : '';

        const { mentionChar, mentionCharIndex } = getMentionCharIndex(
            textBeforeCursor,
            this.options.mentionDenotationChars!,
            this.options.isolateCharacter!,
            this.options.allowInlineMentionChar!
        );

        if (
            mentionChar !== null &&
            hasValidMentionCharIndex(
                mentionCharIndex,
                textBeforeCursor,
                this.options.isolateCharacter!,
                textPrefix
            )
        ) {

            const mentionCharPos = this.cursorPos - (textBeforeCursor.length - mentionCharIndex);
            const textAfter = textBeforeCursor.substring(mentionCharIndex + mentionChar.length);

            if (
                textAfter.length >= this.options.minChars! &&
                hasValidChars(textAfter, this.getAllowedCharsRegex(mentionChar))
            ) {
                // mentionChar, data, searchTerm
                return [mentionChar, mentionCharPos, textAfter] as const;
            }
        }

        return null;
    }

    getTextBeforeCursor() {
        const startPos = Math.max(0, (this.cursorPos ?? 0) - this.options.maxChars!);
        const textBeforeCursorPos = this.quill.getText(startPos, (this.cursorPos ?? 0) - startPos);
        return textBeforeCursorPos;
    }

    getCurrentLine() {
        if (this.cursorPos == null) return '';

        // Get all text from the start of document up to cursor
        const textUpToCursor = this.quill.getText(0, this.cursorPos);

        // Split by newlines and get the last line (current line)
        const lines = textUpToCursor.split('\n');
        return lines[lines.length - 1];
    }

    onSomethingChange() {
        const range = this.quill.getSelection();
        if (range == null) return;

        this.cursorPos = range.index;

        const [mentionChar, mentionCharPos, textAfter] = this.getMentionCharAndTextInfo() ?? [];

        if (!!mentionChar) {
            this.showWidget(mentionChar, mentionCharPos, textAfter);
        }
        else {
            this.hideWidget();
        }
    }

    getAllowedCharsRegex(denotationChar: string): RegExp {
        if (this.options.allowedChars instanceof RegExp) {
            return this.options.allowedChars!;
        }
        else {
            return this.options.allowedChars?.(denotationChar) ?? /^[a-zA-Z0-9_]*$/;
        }
    }

    onTextChange(delta: Delta, oldContent: Delta, source: EmitterSource) {
        if (source === 'user') {
            setTimeout(this.onSomethingChange.bind(this), 50);
        }
    }

    onSelectionChange(range: Range | null) {
        if (range !== null && range.length === 0) {
            this.onSomethingChange();
        }
        else {
            if (range !== null) {
                this.hideWidget();
            }
        }
    }

    getSuggestedWidgetPositioning(mentionCharPos: number) {

        const containerPos = this.quill.container.getBoundingClientRect();
        const mentionCharBounds = this.quill.getBounds(mentionCharPos);
        if (mentionCharBounds === null) {
            return null;
        }

        const relativePosition = Rect.fromLike(mentionCharBounds).bottomLeft();
        const absolutePosition = Point.from(containerPos).add(relativePosition);

        return { relativePosition, absolutePosition };
    }
}
